<template>
  <div>
    <import-sidebar
      :is-import-sidebar-active.sync="isImportSidebarActive"
      @refetch-data="refetchData"
    />

    <upload-images-modal
      :is-upload-images-modal-active.sync="isUploadImagesModalActive"
      :reject-item="selectedReject"
      @refetch-data="refetchData"
    />

    <b-card
      no-body
      class="mb-0"
    >
      <div class="m-2">
        <b-row>
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label>Show</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label>entries</label>
          </b-col>

          <b-col
            cols="12"
            md="6"
          >
            <div class="d-flex align-items-center justify-content-end flex-wrap">
              <b-form-input
                class="d-inline-block mr-1 mb-1 mb-md-0"
                placeholder="Cari item code / nama produk / alasan..."
                @input="onSearchInput"
              />
              <b-button
                variant="primary"
                @click="isImportSidebarActive = true"
              >
                Import Excel
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="refQcRejectListTable"
        class="position-relative"
        :items="fetchQcRejects"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        :sort-desc.sync="isSortDirDesc"
        show-empty
        empty-text="Belum ada data QC Reject"
        :bordered="true"
        :hover="true"
      >
        <template #cell(note)="data">
          {{ data.value || '-' }}
        </template>

        <template #cell(sale_price)="data">
          {{ formatCurrency(data.value) }}
        </template>

        <template #cell(reported_at)="data">
          {{ formatDate(data.value) }}
        </template>

        <template #cell(images_count)="data">
          <b-badge variant="light-primary">
            {{ data.value || 0 }} foto
          </b-badge>
        </template>

        <template #cell(actions)="data">
          <b-button
            size="sm"
            variant="outline-primary"
            @click="openUploadModal(data.item)"
          >
            Upload Foto
          </b-button>
        </template>
      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">Showing {{ dataMeta.from }} to {{ dataMeta.to }} of {{ dataMeta.of }} entries</span>
          </b-col>

          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalItems"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
/* eslint-disable import/no-cycle */
import {
  BBadge,
  BButton,
  BCard,
  BCol,
  BFormInput,
  BPagination,
  BRow,
  BTable,
} from 'bootstrap-vue'
import { ref, onUnmounted } from '@vue/composition-api'
import store from '@/store'
import vSelect from 'vue-select'
import useQcRejectList from './useQcRejectList'
import qcRejectStoreModule from './qcRejectStoreModule'
import ImportSidebar from './ImportSidebar.vue'
import UploadImagesModal from './UploadImagesModal.vue'

export default {
  components: {
    BBadge,
    BButton,
    BCard,
    BCol,
    BFormInput,
    BPagination,
    BRow,
    BTable,
    ImportSidebar,
    UploadImagesModal,
    vSelect,
  },
  setup() {
    const QC_REJECT_STORE_MODULE_NAME = 'app-qc-reject'
    let searchDebounceTimeout = null

    if (!store.hasModule(QC_REJECT_STORE_MODULE_NAME)) {
      store.registerModule(QC_REJECT_STORE_MODULE_NAME, qcRejectStoreModule)
    }

    onUnmounted(() => {
      if (searchDebounceTimeout) {
        clearTimeout(searchDebounceTimeout)
      }

      if (store.hasModule(QC_REJECT_STORE_MODULE_NAME)) {
        store.unregisterModule(QC_REJECT_STORE_MODULE_NAME)
      }
    })

    const isImportSidebarActive = ref(false)
    const isUploadImagesModalActive = ref(false)
    const selectedReject = ref(null)

    const {
      fetchQcRejects,
      tableColumns,
      perPage,
      currentPage,
      totalItems,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refQcRejectListTable,
      refetchData,
    } = useQcRejectList()

    const onSearchInput = value => {
      if (searchDebounceTimeout) {
        clearTimeout(searchDebounceTimeout)
      }

      searchDebounceTimeout = setTimeout(() => {
        searchQuery.value = value
      }, 400)
    }

    const openUploadModal = item => {
      selectedReject.value = item
      isUploadImagesModalActive.value = true
    }

    const formatDate = value => {
      if (!value) {
        return '-'
      }

      return new Date(value).toLocaleDateString('id-ID')
    }

    const formatCurrency = value => {
      if (!value) {
        return '-'
      }

      return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        maximumFractionDigits: 0,
      }).format(value)
    }

    return {
      currentPage,
      dataMeta,
      fetchQcRejects,
      formatDate,
      formatCurrency,
      isImportSidebarActive,
      isSortDirDesc,
      isUploadImagesModalActive,
      onSearchInput,
      openUploadModal,
      perPage,
      perPageOptions,
      refQcRejectListTable,
      refetchData,
      selectedReject,
      sortBy,
      tableColumns,
      totalItems,
    }
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import '~@core/scss/vue/libs/vue-select.scss';
</style>
